import React, { useEffect, useMemo, useReducer, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import Form from "react-bootstrap/esm/Form";
import { useParams } from "react-router-dom";
import { UploadFunctions } from "@sssuite-js-packages/file-utility";
import { CustomModal } from "../../Common/Modal/CustomModal";
import { IRecipientModel } from "src/Core/ViewModels/Message/RecipientModel";
import {
    AuthenticationMethod,
    FolderType,
    MessageActions,
    MessageTab,
    RecipientType,
    SettingMode,
    ValidationType,
} from "src/Core/Common/Enums";
import AttachmentUploader, { IFileUploadModel } from "../../Parts/AttachmentUploader";
import { ApplicationState } from "src/store";
import { useDispatch, useSelector } from "react-redux";
import { initialSentMessage, ISentMessageModel } from "src/Core/ViewModels/Message/SentMessageModel";
import {
    initialRecipientQuestionAnswer,
    QnAActions,
    recipientQuestionAnswerReducer,
} from "src/reducers/recipientQuestionAnswerReducer";
import {
    addMessageSecurityQuestions,
    fetchMessageSecurityQuestions,
    getRecipientDefaultQuestionAndAnswer,
} from "src/actionCreators/securityQuestionCreators";
import { ISecurityQuestionAnswer } from "src/Core/Domain/Compose/IComposeModel";
import {
    fetchAuthenticationMethodSettings,
    fetchMessageSecurityQuestionSettings,
    fetchRetentionSettings
} from "src/actionCreators/adminSettingsActionCreators";
import { fetchUserSettings } from "src/actionCreators/userSettingsActionCreators";
import {
    fetchAuthRetentionPeriods,
    fetchComposeRecipients,
    fetchUploadAttachmentSasLink,
    removeAttachment,
} from "src/actionCreators/composeActionCreators";
import {
    isValidEmailAddress,
    IValidationMessage,
    validateComposeMessageModel,
    validateQuickReplyModel,
    validateRecipientQnA,
} from "src/helper/Validations";
import { MAX_SUBJECT_LENGTH } from "src/Core/Common/Constants";
import { AppNotifier } from "../../Common/Notification/AppNotifier";
import {
    initialSentMessageRecipientModel,
    ISentMessageRecipientModel,
} from "src/Core/ViewModels/Message/SentMessageRecipientModel";
import { IFileModel } from "src/Core/ViewModels/File/FileModel";
import { ISentMailMessage } from "src/Core/ViewModels/Message/SentMailMessageModel";
import { forwardSentMessage, quickReplyMessage, replySentMessage } from "src/actionCreators/sentMessageActionCreators";
import { IMessageSecurityQuestion } from "src/Core/ViewModels/Message/MessageSecurityQuestionModel";
import { ComposeIcon, ComposeSettingIcon } from "src/assets/images/svg/IconCollection";
import { validateFileSize, isMaxUploadSizeLimitExceeded } from "../Helper/HelperFunctions";
import Recipient from "src/components/Compose/Parts/Recipient";
import ComposeOptions from "../Parts/ComposeOptions";
import ComposePreview from "../Parts/ComposePreview";
import AttachmentList from "../Parts/AttachmentList";
import { IReceivedMailMessage } from "src/Core/ViewModels/Message/ReceivedMailMessageModel";
import { IReceivedMessageRecipientModel } from "src/Core/ViewModels/Message/ReceivedMessageRecipientModel";
import { IUserProfile } from "src/Core/ViewModels/Profile/ProfileViewModel";
import "./style.css";
import { getFileName, getResourceLocators, getUserIdFromEmail } from "src/helper/HelperFunctions";
import { ResourceIdLocators } from "src/helper/ResourceIdLocators";
import { ToasterMessages } from "src/helper/ToasterMessages";
import { IFileToUpload } from "@sssuite-js-packages/file-utility/dist/FileUtility/FileUpload/FileUpload.model";
import LexicalEditor from "src/components/Common/ui/LexicalEditor/LexicalEditor";

const ComposeModal: React.FC = () => {
    const [openTab, toggleTab] = useState<MessageTab>(MessageTab.Compose);
    const [isInvalidData, setIsInvalidData] = useState(false);
    const [confirmSend, setConfirmSend] = useState({
        show: false,
        sendAnyway: false,
        message: "",
    });
    const [selectedMessage, setSelectedMessage] = useState<IReceivedMailMessage | ISentMailMessage>();
    const [messageBody, setMessageBody] = useState("");
    const [subject, setSubject] = useState("");
    const [title, setTitle] = useState("");
    const [attachments, setAttachments] = useState<IFileUploadModel[]>([]);
    const [showDragAndDrop, setShowDragAndDrop] = useState(false);
    const [sentMessage, setSentMessage] = useState<ISentMessageModel>(initialSentMessage);
    const [ccRecipients, setCcRecipients] = useState<IRecipientModel[]>([]);
    const [toRecipients, setToRecipients] = useState<IRecipientModel[]>([]);
    const [isAuthMethodUpdated, setIsAuthMethodUpdated] = useState(false);
    const [selectedTaxYear, setSelectedTaxYear] = useState<number>();
    const [recipients, dispatchRecipient] = useReducer(recipientQuestionAnswerReducer, []);
    const [isRetentionUpdated, setIsRetentionUpdated] = useState(false);
    const [lexiconEditorKey, setLexiconEditorKey] = useState<string>(uuidv4());
    const fileUploadUtilities = new UploadFunctions();

    const userSettingsStore = useSelector((state: ApplicationState) => state.userSettingsState);
    const userProfileState: IUserProfile = useSelector((appState: ApplicationState) => appState.userProfile);

    const profile = useSelector((appState: ApplicationState) => appState.userSettingsState);
    const composeState = useSelector((state: ApplicationState) => state.composeState);
    const securityQuestionState = useSelector((state: ApplicationState) => state.securityQuestionState);
    const selectedInboxMessage = useSelector(
        (appState: ApplicationState) => appState.receivedMessageState.mailData.data
    );
    const selectedSentMessage = useSelector((appState: ApplicationState) => appState.sentMessageState.mailData.data);
    const documentsRef = React.useRef<IFileUploadModel[]>([]);
    documentsRef.current = attachments;

    const dispatch = useDispatch();
    const params = useParams();
    const resourceLocators = useMemo(() => {
        return getResourceLocators(+params.folderType);
    }, [ResourceIdLocators]);

    useEffect(() => {
        composeInitiate();
    }, [toRecipients, ccRecipients]);

    const composeInitiate = () => {
        toggleTab(MessageTab.Compose);
        dispatch(fetchRetentionSettings());
        
        setSentMessage({
            ...sentMessage,
            message: messageBody ?? "",
            subject: subject ?? "",
        });
        let tempRecipients = toRecipients ?? [];
        tempRecipients = ccRecipients ? tempRecipients.concat(ccRecipients) : tempRecipients;
        let i = 0;
        tempRecipients = tempRecipients.map(item => {
            if (item.index === undefined) item.index = i;
            i++;
            return item;
        });

        dispatchRecipient({
            type: QnAActions.SET,
            payload: tempRecipients,
        });

        reloadMessageSecurityQuestions();
        dispatch(fetchAuthenticationMethodSettings());
        dispatch(fetchMessageSecurityQuestionSettings());

        tempRecipients.forEach(recipient => {
            dispatch(
                getRecipientDefaultQuestionAndAnswer(
                    recipient.emailAddress,
                    getUsersDefaultQuestion()?.messageSecurityQuestionId ?? 0,
                    (result: ISecurityQuestionAnswer) => {
                        let toData: IRecipientModel;
                        if (result && result?.securityQuestionId != null) {
                            const securityQuestion = securityQuestionState.securityQuestions.data.find(
                                x => x.messageSecurityQuestionId == result.securityQuestionId
                            );
                            toData = {
                                ...initialRecipientQuestionAnswer,
                                ...recipient,
                                questionId: securityQuestion?.messageSecurityQuestionId,
                                answer: result.answer,
                                question: securityQuestion?.question,
                                isModified: false,
                            };
                            if (sentMessage.authenticationMethod === AuthenticationMethod.QUESTIONANSWER) {
                                setIsInvalidData(false);
                            }
                            dispatchRecipient({ type: QnAActions.UPDATE, payload: toData });
                        } else {
                            if (sentMessage.authenticationMethod === AuthenticationMethod.QUESTIONANSWER) {
                                setIsInvalidData(true);
                            }
                        }
                    }
                )
            );
        });
    };

    function getUsersDefaultQuestion() {
        return (
            securityQuestionState.securityQuestions.data.find(x => x.isDefault) ||
            securityQuestionState.securityQuestions.data[0]
        );
    }

    useEffect(() => {
        setShowDragAndDrop(false);
    }, [attachments]);

    useEffect(() => {
        let questionId = 0;
        if (securityQuestionState.securityQuestions?.data?.length > 0) {
            questionId = getUsersDefaultQuestion()?.messageSecurityQuestionId ?? 0;
        }
    }, [securityQuestionState.securityQuestions.data.length, recipients.length]);

    useEffect(() => {
        attachments.length > 0 && onUploadFile(attachments);
    }, [attachments.length]);

    useEffect(() => {
        if (userSettingsStore.userSettings && userSettingsStore.userSettings.authenticationMethodSettings) {
            setSentMessage({
                ...sentMessage,
                authenticationMethod:
                    userSettingsStore?.userSettings?.authenticationMethodSettings.defaultAuthenticationMethod || 0,
                expiresDays: getRetentionPeriodFromUserSettings(
                    userSettingsStore.userSettings.authenticationMethodSettings.defaultAuthenticationMethod
                ),
            });
        }
        getSelectedTaxYear();
    }, [
        userSettingsStore?.userSettings,
        userSettingsStore?.userSettings?.authenticationMethodSettings,
        composeState.authRetentions,
    ]);

    useEffect(() => {
        switch (+params.folderType) {
            case FolderType.INBOX:
                setSelectedMessage(selectedInboxMessage);
                break;
            case FolderType.SENT:
                setSelectedMessage(selectedSentMessage);
                break;
            default:
                setSelectedMessage(selectedInboxMessage);
                break;
        }
    }, [selectedInboxMessage, selectedSentMessage]);

    useEffect(() => {
        if (selectedMessage) {
            switch (+params.messageActionType) {
                case MessageActions.Reply:
                    setSubject("RE: " + selectedMessage.messageDetail.subject);
                    setToRecipients(convertRecipientToContact(selectedMessage));
                    setCcRecipients([]);
                    setTitle("Reply Message");
                    setMessageBody(getLexicalMessageBody(selectedMessage.messageDetail.message));
                    setAttachments([]);
                    setLexiconEditorKey(uuidv4());
                    break;
                case MessageActions.ReplyAll:
                    setSubject("RE: " + selectedMessage.messageDetail.subject);
                    setToRecipients(convertRecipientToContact(selectedMessage));
                    setCcRecipients(convertRecipientCcContact(selectedMessage, userProfileState));
                    setTitle("Reply Message");
                    setMessageBody(getLexicalMessageBody(selectedMessage.messageDetail.message));
                    setAttachments([]);
                    setLexiconEditorKey(uuidv4());
                    break;
                case MessageActions.Forward:
                    setSubject("FW: " + selectedMessage.messageDetail.subject);
                    setMessageBody(getLexicalMessageBody(selectedMessage.messageDetail.message));
                    setToRecipients([]);
                    setCcRecipients([]);
                    setTitle("Forward Message");
                    setAttachments(convertToAttachments(selectedMessage.attachments));
                    setLexiconEditorKey(uuidv4());
                    break;
                case MessageActions.QuickReply:
                    setSubject("Quick Reply: " + selectedMessage.messageDetail.subject);
                    setMessageBody("");
                    setToRecipients(convertRecipientToContact(selectedMessage));
                    setCcRecipients([]);
                    setTitle("Quick Reply");
                    setAttachments([]);
                    setLexiconEditorKey(uuidv4());
                    break;
                default:
                    break;
            }
        }
    }, [selectedMessage]);

    useEffect(() => {
        dispatch(fetchUserSettings());
        dispatch(fetchAuthRetentionPeriods(AuthenticationMethod.ACCESSCODE));

        const dropArea = document.getElementsByClassName("modal-content")[0];

        if (dropArea) {
            dropArea.addEventListener("dragenter", e => {
                e.preventDefault();
                e.stopPropagation();
                !showDragAndDrop && setShowDragAndDrop(true);
            });

            dropArea.addEventListener("dragleave", e => {
                e.preventDefault();
                e.stopPropagation();
                showDragAndDrop && setShowDragAndDrop(false);
            });

            dropArea.addEventListener("dragover", e => {
                e.preventDefault();
                e.stopPropagation();
                setShowDragAndDrop(true);
            });

            dropArea.addEventListener("drop", (e: any) => {
                if (e?.target?.className === "dragging") {
                    setShowDragAndDrop(true);
                } else {
                    e.preventDefault();
                    e.stopPropagation();
                    setShowDragAndDrop(false);
                }
            });
        }
    }, []);

    useEffect(() => {
        attachments.length > 0 && onUploadFile(attachments);
    }, [documentsRef.current.length]);

    useEffect(() => {
        validateOnChangeTab();
    }, [openTab, sentMessage.expiresDays]);

    useEffect(() => {
        confirmSend.sendAnyway && handleMessageSend();
    }, [confirmSend]);

    // Below fix is temporary. It needs to be handled from the LexicalEditor component itself.
    const getLexicalMessageBody = (messageBody: string) => {
        if (!messageBody.startsWith("<p")) {
            return `<p>${messageBody}</p>`;
        }
        return messageBody;
    };
    
    const reloadMessageSecurityQuestions = () => dispatch(fetchMessageSecurityQuestions(SettingMode.BOTH));

    const handleTabClick = (e: any, tab: MessageTab) => {
        e.stopPropagation();
        toggleTab(tab);
    };

    const handleMessageBodyChange = (value: any) => {
        setMessageBody(value);
        setSentMessage((prevSentMessage: ISentMessageModel) => ({
            ...prevSentMessage,
            message: value,
        }));
    };

    const handleSubjectChange = (value: string) => {
        setSubject(value);
        setSentMessage({
            ...sentMessage,
            subject: value,
        });
    };

    const handleRecipientChange = (data: any, type: RecipientType) => {
        const email = data.option?.value?.trim();
        if (recipients.find((x: IRecipientModel) => x.emailAddress === email)) {
            return;
        }
        switch (data.action) {
            case "select-option":
            case "create-option":
                if (isValidEmailAddress(email)) {
                    const label =
                        isValidEmailAddress(data.option.label) && email.includes(data.option.label)
                            ? ""
                            : data.option.label;
                    let toData: IRecipientModel;
                    dispatch(
                        getRecipientDefaultQuestionAndAnswer(
                            email,
                            getUsersDefaultQuestion()?.messageSecurityQuestionId ?? 0,
                            (result: ISecurityQuestionAnswer) => {
                                if (result && result?.securityQuestionId != null) {
                                    const securityQuestion = securityQuestionState.securityQuestions.data.find(
                                        x => x.messageSecurityQuestionId == result.securityQuestionId
                                    );
                                    toData = {
                                        ...initialRecipientQuestionAnswer,
                                        firstName: label.split(" ")[0] ?? "",
                                        lastName: label.split(" ")[1] ?? "",
                                        emailAddress: email,
                                        recipientType: type,
                                        userId: getUserIdFromEmail(composeState.recipients.data, email),
                                        questionId: securityQuestion?.messageSecurityQuestionId,
                                        answer: result.answer,
                                        question: securityQuestion?.question,
                                        isModified: false,
                                    };
                                    if (sentMessage.authenticationMethod === AuthenticationMethod.QUESTIONANSWER) {
                                        setIsInvalidData(false);
                                    }
                                    dispatchRecipient({ type: QnAActions.UPDATE, payload: toData });
                                } else {
                                    if (sentMessage.authenticationMethod === AuthenticationMethod.QUESTIONANSWER) {
                                        setIsInvalidData(true);
                                    }
                                }
                            }
                        )
                    );

                    toData = {
                        ...initialRecipientQuestionAnswer,
                        firstName: label.split(" ")[0] ?? "",
                        lastName: label.split(" ")[1] ?? "",
                        emailAddress: email,
                        recipientType: type,
                        userId: getUserIdFromEmail(composeState.recipients.data, email),
                        questionId: getUsersDefaultQuestion().messageSecurityQuestionId,
                        isModified: false,
                        answer: "",
                        question: getUsersDefaultQuestion().question,
                    };
                    dispatchRecipient({ type: QnAActions.ADD, payload: toData });
                } else {
                    AppNotifier.Warning(ToasterMessages.WARNING.INVALID_EMAIL_FORMAT);
                }
                break;
            case "remove-value":
            case "pop-value":
                if (data && data.removedValue) {
                    const emailAddress = data.removedValue.value;
                    dispatchRecipient({
                        type: QnAActions.REMOVE,
                        payload: { emailAddress },
                    });
                }
                break;
        }
    };

    const convertRecipientToContact = (messageModel: IReceivedMailMessage | ISentMailMessage) => {
        const recipientQA = messageModel.recipientSecurityQuestions.find(
            x => x.recipientEmail.toLowerCase() === messageModel.messageDetail.senderEmail.toLowerCase()
        );
        return [
            {
                firstName: messageModel.messageDetail.senderFirstName,
                lastName: messageModel.messageDetail.senderLastName,
                emailAddress: messageModel.messageDetail.senderEmail,
                recipientType: RecipientType.ToRecipient,
                userId: messageModel.messageDetail.senderId,
                questionId: recipientQA?.securityQuestionId,
                question: recipientQA?.question,
                answer: recipientQA?.answer,
            },
        ];
    };

    const convertRecipientCcContact = (messageModel: IReceivedMailMessage | ISentMailMessage, userProfile: any) => {
        const tempRecipients: any[] = messageModel.recipients;
        const ccRecipients = tempRecipients.filter(
            (recipient: IReceivedMessageRecipientModel | ISentMessageRecipientModel) => {
                return recipient.email !== userProfile.emailAddress;
            }
        );
        return ccRecipients.map((v: IReceivedMessageRecipientModel | ISentMessageRecipientModel) => ({
            firstName: v.firstName,
            lastName: v.lastName,
            emailAddress: v.email,
            recipientType: v.recipientType,
            userId: v.recipientId,
            questionId: messageModel.recipientSecurityQuestions.find(
                x => x.recipientEmail.toLowerCase() === v.email.toLowerCase()
            )?.securityQuestionId,
            question: messageModel.recipientSecurityQuestions.find(
                x => x.recipientEmail.toLowerCase() === v.email.toLowerCase()
            )?.question,
            answer: messageModel.recipientSecurityQuestions.find(
                x => x.recipientEmail.toLowerCase() === v.email.toLowerCase()
            )?.answer,
        }));
    };

    const convertToAttachments = (messageAttachments: IFileModel[]) => {
        const newFiles: IFileUploadModel[] = [];
        Array.from(messageAttachments).forEach((item: IFileModel) => {
            const fileName = getFileName(item.name);
            newFiles.push({
                file: new File([fileName], fileName),
                guid: item.fileGuid,
                isUploaded: true,
                uploadProgress: 0,
                fileSize: item.size,
                fileId: item.fileId,
            });
        });

        return newFiles;
    };

    const handleRecipientQuestionAnswer = (result: ISecurityQuestionAnswer) => {
        const recipient = recipients.find(x => x.emailAddress === result.recipientEmail);
        if (recipient && recipient.index) {
            const data: IRecipientModel = {
                firstName: recipient.firstName,
                lastName: recipient.lastName,
                emailAddress: recipient.emailAddress,
                questionId: result.securityQuestionId,
                question: securityQuestionState.securityQuestions.data.find(
                    x => x.messageSecurityQuestionId === result.securityQuestionId
                )?.question,
                answer: result.answer,
                recipientType: RecipientType.ToRecipient,
            };

            dispatchRecipient({
                type: QnAActions.SAVE,
                payload: { data, index: recipient.index },
            });
        }
    };

    const handleOnDropFiles = (e: any, callback?: any) => {
        e.preventDefault();
        e.stopPropagation();
        const newFiles: IFileUploadModel[] = [...attachments];
        const files: File[] = e.target.files || e.dataTransfer.files;

        let totalSize = 0;
        Array.from(files).forEach((item: File) => {
            totalSize += item.size;
        });

        attachments.forEach(attachment => {
            totalSize += attachment.file.size;
        });

        const filesArray = Array.from(files);
        for (let i = 0; i < filesArray.length; i++) {
            const item = filesArray[i];
            if (!validateFileSize(item)) {
                break;
            }
            if (!isMaxUploadSizeLimitExceeded(totalSize)) {
                break;
            }
            newFiles.push({
                file: item,
                uploadProgress: 0,
                guid: uuidv4(),
                isUploaded: false,
                displayName: getFileName(item.name),
            });
        }

        setAttachments(newFiles);
        if (callback && typeof callback === "function") {
            callback();
        }
    };

    const onUploadFile = (documents: IFileUploadModel[]) => {
        for (let i = 0; i < documents.length; i++) {
            const fileName = getFileName(documents[i].file.name);
            if (!documents[i].isUploaded) {
                dispatch(
                    fetchUploadAttachmentSasLink(documents[i].guid, (data: any) => {
                        fileUploadUtilities.uploadFile(documents[i].file, data, fileName, uploadProgressCallback, uploadCompleteCallback);
                    })
                );
            }
        }
    };

    const uploadProgressCallback = (percent: number, fileToUpload: any) => {
        const updatedState = documentsRef.current.map(item => {
            if (item.guid === fileToUpload.fileGUID) {
                item.uploadProgress = (percent == 100 ? percent - 10 : percent) / 100;
            }
            return item;
        });
        setAttachments(updatedState);
    };

    const uploadCompleteCallback = (fileToUpload: IFileToUpload, errorMessage?: string) => {
        if (fileToUpload.uploadFailed) {
            const attachments = documentsRef.current.filter(file => file.guid != fileToUpload.fileGUID);
            if (errorMessage && attachments.length < documentsRef.current.length) {
                AppNotifier.Warning(errorMessage);
            }
            setAttachments(attachments);
            return;
        }
        const updatedState = documentsRef.current.map(item => {
            if (item.guid === fileToUpload.fileGUID && !fileToUpload.uploadFailed) {
                item.uploadProgress = 1;
                item.isUploaded = true;
            }
            return item;
        });
        setAttachments(updatedState);
    };

    const handleOnRemoveAttachment = (guid: string, fileId?: number) => {
        if (!fileId) {
            dispatch(removeAttachment(guid, removeAttachmentFromState));
        } else {
            removeAttachmentFromState(guid);
        }
    };

    const removeAttachmentFromState = (guid: string) => {
        let updatedFiles: IFileUploadModel[] = [...attachments];
        updatedFiles = updatedFiles.filter(x => x.guid !== guid);
        setAttachments(updatedFiles);
    };

    const handleOnChangeRetention = (option: any) => {
        const tempMessage: ISentMessageModel = {
            ...sentMessage,
            expiresDays: +option.value,
        };
        setSentMessage(tempMessage);
        setIsRetentionUpdated(true);
    };

    const handleOnChangeTaxYear = (option: any) => {
        const tempMessage: ISentMessageModel = {
            ...sentMessage,
            taxYear: option.value,
        };
         setSentMessage(tempMessage);
         setSelectedTaxYear(option.value);
    };

    const handleOnChangeAuthentication = (e: any) => {
        let tempMessage: ISentMessageModel = {
            ...sentMessage,
        };
        if (!isRetentionUpdated) {
            tempMessage = {
                ...sentMessage,
                authenticationMethod: +e.value,
                expiresDays: getRetentionPeriodFromUserSettings(+e.value),
            };
        } else {
            tempMessage = {
                ...sentMessage,
                authenticationMethod: +e.value,
            };
        }
        setSentMessage(tempMessage);
    };

    const getRetentionPeriodFromUserSettings = (authenticationMethod: AuthenticationMethod) => {
        if (!profile) return 0;

        switch (authenticationMethod) {
            case AuthenticationMethod.ACCESSCODE:
                return getSelectedOption(
                    userSettingsStore?.userSettings?.retentionSettings.defaultRetentionPeriods.accessCode
                );
            case AuthenticationMethod.QUESTIONANSWER:
                return getSelectedOption(
                    userSettingsStore?.userSettings?.retentionSettings.defaultRetentionPeriods.questionAndAnswer
                );
            // case AuthenticationMethod.LOGINPASSWORD:
            //     return getSelectedOption(
            //         profile?.userSettings?.retentionSettings.defaultRetentionPeriods.loginTransfer
            //     );
            default:
                return 0;
        }
    };

    const getSelectedOption = (value?: number) => {
        if (value) {
            const result = composeState.authRetentions.data.find(rp => rp.retentionId === value);

            if (result) return result.days;
            else return 0;
        }
        return 0;
    };

    const getSelectedTaxYear = (): void => {

        let _selectedTaxYear: number ;

        if(+params.messageActionType === MessageActions.QuickReply) {
            if(selectedMessage?.messageDetail?.taxYear) {
            _selectedTaxYear = selectedMessage.messageDetail?.taxYear;
            }
        }
        else{
            if(userSettingsStore.userSettings?.taxYearSettings?.defaultTaxYear) {
             _selectedTaxYear = userSettingsStore.userSettings?.taxYearSettings?.defaultTaxYear;
            }
            else
            {
             const currentDate = new Date();
             const mayFirst = new Date(currentDate.getFullYear(), 4, 1); // Month is 0-based, so 4 = May
             _selectedTaxYear = currentDate >= mayFirst ? currentDate.getFullYear() : currentDate.getFullYear() - 1;
            }
        }
            setSelectedTaxYear(_selectedTaxYear);
    };

    const handleMessageSend = () => {
        const sentRecepients: ISentMessageRecipientModel[] = [];
        const questionAnswersForRecipients: ISecurityQuestionAnswer[] = [];
        sentMessage.taxYear = selectedTaxYear ?? 0;

        recipients.map((item: IRecipientModel) => {
            sentRecepients.push({
                ...initialSentMessageRecipientModel,
                firstName: item.firstName,
                lastName: item.lastName,
                email: item.emailAddress,
                recipientType: item.recipientType,
                expiresDays: sentMessage.expiresDays,
                recipientId: item.userId || undefined,
                securityQuestion: item.question?.trim() ?? "",
                securityAnswer: item.answer?.trim() ?? "",
            });

            if (sentMessage.authenticationMethod == AuthenticationMethod.QUESTIONANSWER) {
                questionAnswersForRecipients.push({
                    securityQuestionId: item.questionId ?? 0,
                    answer: item.answer?.trim() ?? "",
                    recipientEmail: item.emailAddress,
                    question: item.question?.trim() ?? "",
                });
            }
        });

        const tempAttachments: IFileModel[] = attachments.map(item => ({
            fileId: item.fileId,
            fileGuid: item.guid,
            isUploadComplete: item.isUploaded,
            name: getFileName(item.file.name),
            size: item.fileSize ?? item.file.size,
            uploadProgress: item.uploadProgress * 100,
        }));

        const message: ISentMailMessage = {
            messageDetail: sentMessage,
            recipients: sentRecepients,
            attachments: tempAttachments,
            recipientSecurityQuestions: questionAnswersForRecipients,
        };

        validateMessage(message);
    };

    const sendMailMessage = (message: ISentMailMessage) => {
        switch (+params.messageActionType) {
            case MessageActions.Reply:
            case MessageActions.ReplyAll:
                dispatch(
                    replySentMessage(
                        message,
                        "",
                        +params.folderType == FolderType.INBOX || +params.folderType == FolderType.ARCHIVE
                            ? selectedInboxMessage.messageDetail.receivedMessageId
                            : selectedSentMessage.messageDetail.sentMessageId,
                        +params.folderType,
                        +params.folderType == FolderType.INBOX || +params.folderType == FolderType.ARCHIVE
                        ? selectedInboxMessage.messageDetail.threadId
                        : selectedSentMessage.messageDetail.threadId,
                        handleSuccess,
                        handleReplyMessageError,
                        +params.messageActionType == MessageActions.Reply
                            ? resourceLocators["MsgDtlBtnReply"]
                            : resourceLocators["MsgDtlBtnReplyAll"],
                        +params.folderType == FolderType.INBOX || +params.folderType == FolderType.ARCHIVE
                            ? selectedInboxMessage.messageDetail.folderId
                            : selectedSentMessage.messageDetail.associatedFolderId??selectedSentMessage.messageDetail.folderId      
                    )
                );
                break;
            case MessageActions.Forward:
                dispatch(
                    forwardSentMessage(
                        message,
                        +params.folderType == FolderType.INBOX || +params.folderType == FolderType.ARCHIVE
                            ? selectedInboxMessage.messageDetail.receivedMessageId
                            : selectedSentMessage.messageDetail.sentMessageId,
                        +params.folderType,
                        handleSuccess,
                        handleForwardMessageError,
                        resourceLocators["MsgDtlBtnForward"],
                        +params.folderType == FolderType.INBOX || +params.folderType == FolderType.ARCHIVE
                            ? selectedInboxMessage.messageDetail.folderId
                            : selectedSentMessage.messageDetail.associatedFolderId??selectedSentMessage.messageDetail.folderId     
                    )
                );
                break;
            case MessageActions.QuickReply:
                quickReplyMessage(
                    message,
                    handleSuccess,
                    handleReplyMessageError,
                    resourceLocators["MsgDtlBtnReply"],
                    +params.folderType == FolderType.INBOX
                        ? selectedInboxMessage.messageDetail.receivedMessageId
                        : selectedSentMessage.messageDetail.sentMessageId,
                    +params.folderType,
                    +params.folderType == FolderType.INBOX
                        ? selectedInboxMessage.messageDetail.threadId
                        : selectedSentMessage.messageDetail.threadId,
                );
                break;
        }
    };

    const validateMessage = (message: ISentMailMessage) => {
        const validationMessage: IValidationMessage = 
        +params.messageActionType === MessageActions.QuickReply 
            ? validateQuickReplyModel(message) 
            : validateComposeMessageModel(message, recipients);
        switch (validationMessage.validationType) {
            case ValidationType.WARNING:
                if (!confirmSend.sendAnyway) {
                    setConfirmSend({
                        show: true,
                        sendAnyway: false,
                        message: validationMessage.message,
                    });
                } else {
                    sendMailMessage(message);
                }
                break;
            case ValidationType.INVALID:
                AppNotifier.Warning(validationMessage.message);
                break;
            case ValidationType.VALID:
                sendMailMessage(message);
                break;
            default:
                break;
        }
    };

    const handleOnHideConfirmSend = () => {
        setConfirmSend({ show: false, sendAnyway: false, message: "" });
    };

    const handleSuccess = () => {
        AppNotifier.Success(
            +params.messageActionType === MessageActions.QuickReply
                ? ToasterMessages.SUCCESS.QUICK_REPLY
                : ToasterMessages.SUCCESS.SEND_MESSAGE
        );        dispatchRecipient({
            type: QnAActions.SET,
            payload: [],
        });
        //added delay of 1s for dialog closes before toast notification
        setTimeout(() => {
            Office.context.ui.messageParent("close");
        }, 1000);
    };

    const handleReplyMessageError = () => {
        AppNotifier.Error(
            +params.messageActionType === MessageActions.QuickReply
                ? ToasterMessages.ERROR.QUICK_REPLY_FAILED
                : ToasterMessages.ERROR.SEND_MESSAGE_FAILED
        );        
        setAttachments([]);
    };

    const handleForwardMessageError = () => {
        AppNotifier.Error(ToasterMessages.ERROR.SEND_MESSAGE_FAILED);
        setAttachments(attachments => attachments.filter(a => a.fileId != undefined));
    };

    const handleAddRow = () => {
        dispatchRecipient({
            type: QnAActions.ADD,
            payload: initialRecipientQuestionAnswer,
        });
    };

    const handleRemoveRow = (recipient: IRecipientModel) => {
        dispatchRecipient({
            type: QnAActions.REMOVE,
            payload: { emailAddress: recipient.emailAddress, index: recipient.index },
        });
    };

    const handleEditRow = (recipient: IRecipientModel) => {
        dispatchRecipient({
            type: QnAActions.EDIT,
            payload: { index: recipient.index },
        });
    };

    const handleSaveRow = (recipient: IRecipientModel, data: IRecipientModel) => {
        dispatchRecipient({
            type: QnAActions.SAVE,
            payload: { data, index: recipient.index },
        });
    };

    const handleAddSecurityQuestion = (
        securityQuestion: IMessageSecurityQuestion,
        isUserSecurityQuestion: boolean,
        callback?: any
    ) => {
        dispatch(addMessageSecurityQuestions(securityQuestion, isUserSecurityQuestion, callback));
    };

    const validateOnChangeTab = () => {
        if (openTab === MessageTab.Compose) {
            let isValid = true;
            if (sentMessage.authenticationMethod === AuthenticationMethod.QUESTIONANSWER) {
                for (let i = 0; i < recipients.length; i++) {
                    isValid = validateRecipientQnA(recipients[i], recipients, false);
                }
            }
            if (sentMessage.expiresDays < 0) {
                isValid = false;
            }
            setIsInvalidData(!isValid);
        }
    };

    const getSelectedRetention = (): number => {
        let selectedRetention: number | undefined = sentMessage.expiresDays;
        if (!selectedRetention) {
            selectedRetention = composeState.authRetentions.data.find(x => x.isDefault)?.days;
        }
        return selectedRetention ?? 0;
    };

    const handleRecipientInputChange = (inputValue: string, callback: (data: IRecipientModel[]) => void) => {
        dispatch(
            fetchComposeRecipients(inputValue, (data: IRecipientModel[]) => {
                callback(data);
            })
        );
    };

    return (
        <>
            <div className={`compose-modal ${+params.messageActionType === MessageActions.QuickReply ? "quick-reply-modal" : ""}`}>
                <div className="modal-content">
                    <header>{title}</header>
                    <div className="modal-body" id="droppable">
                        <div className="compose-left-panel">
                            <div
                                className={`side-menu-button ${openTab === MessageTab.Compose ? "active" : ""}`}
                                title="Compose Options"
                                onClick={e => handleTabClick(e, MessageTab.Compose)}
                            >
                                <ComposeIcon
                                    width={26}
                                    height={26}
                                    color={openTab === MessageTab.Compose ? "#88c656" : ""}
                                />
                            </div>
                            <div
                                className={`side-menu-button ${openTab === MessageTab.Options ? "active" : ""}`}
                                title="Message Options"
                                onClick={e => handleTabClick(e, MessageTab.Options)}
                            >
                                <ComposeSettingIcon
                                    width={36}
                                    height={36}
                                    color={openTab === MessageTab.Options ? "#88c656" : ""}
                                />
                                {isInvalidData && <span className="fas fa-exclamation compose-tab-invalid"></span>}
                            </div>
                        </div>
                        <div className="compose-content">
                            <main>
                                <div
                                    style={{
                                        display: openTab === MessageTab.Compose ? "initial" : "none",
                                    }}
                                >
                                    <div className="main-heading">Compose Message</div>

                                    <Recipient
                                        setRecipient={handleRecipientChange}
                                        toRecipients={recipients.filter(
                                            x =>
                                                x.recipientType === RecipientType.ToRecipient &&
                                                isValidEmailAddress(x.emailAddress)
                                        )}
                                        onInputChange={handleRecipientInputChange}
                                        isDisabled={+params.messageActionType === MessageActions.QuickReply}
                                    />

                                    <Form.Group className="subject-wrapper">
                                        <Form.Label>Subject</Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={subject}
                                            maxLength={MAX_SUBJECT_LENGTH}
                                            disabled={+params.messageActionType === MessageActions.QuickReply}
                                            onChange={event => handleSubjectChange(event.target.value)}
                                            title={
                                                +params.messageActionType === MessageActions.QuickReply ? subject : undefined
                                            }
                                        />
                                    </Form.Group>
                                    <div className="compose-body-message drop-area">
                                        <Form.Label
                                            className="message-label"
                                            aria-label="Message"
                                            data-testid={"message-label"}
                                        >
                                            Message
                                        </Form.Label>
                                        {(showDragAndDrop && +params.messageActionType !== MessageActions.QuickReply) && (
                                            <div
                                                className="dragging"
                                                onDrop={e => {
                                                    handleOnDropFiles(e);
                                                }}
                                            >
                                                Drop file(s) here
                                            </div>
                                        )}

                                       {!showDragAndDrop && <div onDragOver={() => setShowDragAndDrop(true)}>
                                        <LexicalEditor
                                            changeStateEditorBody={e => handleMessageBodyChange(e)}
                                            messageBody={messageBody}
                                            key={lexiconEditorKey}
                                        />
                                        </div>}

                                        <AttachmentUploader fromComposeModal={true} onDropFiles={handleOnDropFiles} />
                                        <div className="compose-body-attachments">
                                            <AttachmentList
                                                attachments={attachments}
                                                onRemoveAttachment={handleOnRemoveAttachment}
                                                isRemovingAttachment={composeState.isRemovingAttachment}
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div
                                    style={{
                                        display: openTab === MessageTab.Options ? "initial" : "none",
                                    }}
                                >
                                    <div className="main-heading">Message Options</div>
                                    <ComposeOptions
                                        authRetentions={composeState.authRetentions}
                                        isAuthMethodLoading={composeState.isAuthMethodLoading}
                                        securityQuestions={securityQuestionState.securityQuestions}
                                        onChangeRetention={handleOnChangeRetention}
                                        onChangeTaxYear={handleOnChangeTaxYear}
                                        onChangeAuthentication={handleOnChangeAuthentication}
                                        recipientQuestionAnswer={recipients}
                                        isAuthMethodUpdated={isAuthMethodUpdated}
                                        setIsAuthMethodUpdated={setIsAuthMethodUpdated}
                                        selectedAuthMethod={sentMessage.authenticationMethod}
                                        selectedRetention={getSelectedRetention()}
                                        selectedTaxYear={selectedTaxYear ?? 0}
                                        onAddRow={handleAddRow}
                                        onRemoveRow={handleRemoveRow}
                                        onEditRow={handleEditRow}
                                        onSaveRow={handleSaveRow}
                                        addSecurityQuestion={handleAddSecurityQuestion}
                                        reloadMessageSecurityQuestions={reloadMessageSecurityQuestions}
                                        messageAction={+params.messageActionType}
                                    />
                                </div>

                                <div
                                    style={{
                                        display: openTab === MessageTab.Preview ? "initial" : "none",
                                    }}
                                    className="compose-preview-wrapper"
                                >
                                    <div className="compose-preview-title">
                                        <div className="main-heading">Preview</div>
                                        <button
                                            type="button"
                                            className="btn-close"
                                            aria-label="Close"
                                            onClick={e => handleTabClick(e, MessageTab.Compose)}
                                        ></button>
                                    </div>
                                    <ComposePreview
                                        subject={subject}
                                        selectedTaxYear={selectedTaxYear}
                                        messageBody={messageBody}
                                        toRecipients={recipients.filter(
                                            x => x.recipientType === RecipientType.ToRecipient
                                        )}
                                        ccRecipients={recipients.filter(
                                            x => x.recipientType === RecipientType.CcRecipient
                                        )}
                                        attachments={attachments}
                                    />
                                </div>
                            </main>
                        </div>
                    </div>
                    <footer className="modal-footer">
                    {+params.messageActionType === MessageActions.QuickReply && <button
                        className="button-primary-blue-2-outlined cancel-button"
                        onClick={() => {Office.context.ui.messageParent("dialog-cancelled");}}
                        data-testid="cancel-button"
                    >
                        Cancel
                    </button>}
                        <button
                            className="button-primary-blue-2"
                            onClick={handleMessageSend}
                            disabled={composeState.isSending}
                        >
                            Send
                        </button>
                        {+params.messageActionType !== MessageActions.QuickReply && <div>
                            {openTab !== MessageTab.Preview && (
                                <button
                                    className="button-primary-blue-2-outlined"
                                    onClick={e => handleTabClick(e, MessageTab.Preview)}
                                >
                                    Preview
                                </button>
                            )}
                        </div>}
                    </footer>
                </div>
            </div>
            <CustomModal
                show={confirmSend.show}
                onSubmit={() => setConfirmSend({ show: false, sendAnyway: true, message: "" })}
                confirmButtonName="Send anyway"
                cancelButtonName="Don't Send"
                title="Alert"
                isConfirmModal={true}
                onHide={handleOnHideConfirmSend}
            >
                {confirmSend.message}
            </CustomModal>
            {/* <LoadingModal show={composeState.isSending}>
                <div className="loading-message">Sending Message...</div>
            </LoadingModal> */}
        </>
    );
};

export default ComposeModal;
